import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import QRCode from 'qrcode';
import { passReservations } from '../../services/passApi';
import { Share2, MapPin, AlertCircle, BadgeCheck, Clock, Package } from 'lucide-react';

export default function TicketView() {
  const { qrToken } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const canvasRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    passReservations
      .getTicket(qrToken)
      .then((res) => alive && setData(res.data))
      .catch((err) => alive && setError(err.response?.data?.detail || 'No encontrado'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [qrToken]);

  useEffect(() => {
    if (!data || !canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, qrToken, { width: 260, margin: 2 }, (err) => {
      if (err) console.error(err);
    });
  }, [data, qrToken]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Cargando...</div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <AlertCircle className="w-10 h-10 mx-auto text-orange-500 mb-3" />
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  const { reservation, offer, business } = data;
  const ticketUrl = window.location.href;
  const waText = encodeURIComponent(`Mi ticket Todo en Local Pass: ${ticketUrl}`);
  const benefitName = reservation.benefits?.[0]?.name;
  const isCheckedIn = reservation.checkin_status === 'checked_in';
  const addonsSelected = reservation.addons_selected || [];
  const includedItems = reservation.included_items || offer?.included_items || [];
  const totals = reservation.totals || {};

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-6">
      <div className="max-w-md mx-auto bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="bg-slate-900 text-white px-6 py-5 text-center">
          <p className="text-xs uppercase tracking-wide text-teal-300">Todo en Local Pass</p>
          <h1 className="text-xl font-bold mt-1">{reservation.offer_title}</h1>
          {benefitName && (
            <p className="text-orange-300 text-sm mt-2">{benefitName}</p>
          )}
        </div>

        <div className="p-6 flex flex-col items-center">
          <canvas ref={canvasRef} className="mb-4" />
          <p className="text-xs text-slate-500 break-all text-center">{qrToken}</p>
        </div>

        <div className="px-6 pb-6 space-y-3 text-sm">
          <Row label="Estado" value={
            isCheckedIn ? (
              <span className="inline-flex items-center gap-1 text-green-600 font-semibold">
                <BadgeCheck className="w-4 h-4" /> Validado
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-amber-600 font-semibold">
                <Clock className="w-4 h-4" /> Pendiente de check-in
              </span>
            )
          } />
          {reservation.customer_name && <Row label="Cliente" value={reservation.customer_name} />}
          <Row label="Personas" value={reservation.people_count} />
          {business?.name && <Row label="Negocio" value={business.name} />}
          {business?.address && <Row label="Dirección" value={business.address} />}
          <Row label="Válido hasta" value={new Date(reservation.valid_until).toLocaleDateString()} />
          {reservation.affiliate_code && (
            <Row label="Recomendado por" value={reservation.affiliate_code} />
          )}
          {Number(totals.paid_in_app) > 0 && (
            <Row label="Pagado online" value={`$${Number(totals.paid_in_app).toFixed(2)}`} />
          )}
          {Number(totals.pending_at_business) > 0 && (
            <Row label="Pendiente en negocio" value={`$${Number(totals.pending_at_business).toFixed(2)}`} />
          )}
        </div>

        {/* Included items */}
        {includedItems.length > 0 && (
          <div className="px-6 pb-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-teal-700 mb-1.5 inline-flex items-center gap-1">
              <Package className="w-3.5 h-3.5" /> Incluido
            </p>
            <ul className="space-y-0.5">
              {includedItems.map((it, idx) => (
                <li key={idx} className="text-xs text-slate-700">• {it.name}{it.description ? ` — ${it.description}` : ''}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Addons selected */}
        {addonsSelected.length > 0 && (
          <div className="px-6 pb-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-orange-700 mb-1.5 inline-flex items-center gap-1">
              <Package className="w-3.5 h-3.5" /> Adicionales contratados
            </p>
            <ul className="space-y-0.5">
              {addonsSelected.map((a, idx) => (
                <li key={idx} className="text-xs text-slate-700 flex justify-between">
                  <span>• {a.name} × {a.qty}</span>
                  <span className="font-mono">${Number(a.subtotal || 0).toFixed(2)} {a.pay_mode === 'pay_at_business' ? '(en negocio)' : ''}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="px-6 pb-6 flex flex-col gap-2">
          <a
            href={`https://wa.me/?text=${waText}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-green-500 hover:bg-green-600 text-white font-semibold"
          >
            <Share2 className="w-4 h-4" /> Enviar a mi WhatsApp
          </a>
          {business?.address && (
            <a
              href={`https://maps.google.com/?q=${encodeURIComponent(business.address)}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold"
            >
              <MapPin className="w-4 h-4" /> Abrir en Maps
            </a>
          )}
        </div>

        <p className="px-6 pb-6 text-xs text-slate-400 text-center">
          Guarda este enlace o envíalo a tu WhatsApp para presentar tu QR al llegar.
        </p>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900 text-right">{value}</span>
    </div>
  );
}
